import { Toolchain, ToolchainJob } from './toolchain.models';
import { TableColumn } from '../dynamic-table/dynamic-table.component';
import { EChartsOption } from '../graph/echart-types';

/**
 * Returns the appropriate display name for the given `info` object.
 *
 * If the `info` object represents a toolchain and includes a `displayName` property,
 * that value will be returned. Otherwise, it falls back to the `name` property.
 *
 * @param info - The info object containing name information.
 * @returns A string representing the display name to be shown to the user.
 */
export function getDisplayName(info: Tool | Toolchain): string {
  if ('displayName' in info && info.displayName) {
    return info.displayName;
  }
  return info.name;
}

export function isTool(tool: Tool | Toolchain): tool is Tool {
  return (tool as Tool).inputs !== undefined;
}

export type Tool = {
  toolId: number;
  name: string;
  description: string;
  author: string;
  inputs: ToolParam[];
  outputs: ToolParam[];
  created: string;
  modified: string;
  programLanguage: string;
  project: string;
  toolVersion: string;
  status: string;
};

export type ModificationSetting = {
  allowEdit: boolean;
  allowDelete: boolean;
  allowNew: boolean;
};

export type ToolParam = {
  name: string;
  displayName: string;
  description: string;
  isList: boolean;
  isModifiable: boolean;
  modifications?: Partial<ModificationSetting>;
  param: ToolParamType;
  allowDownload: boolean;
  isOptional?: boolean;
};

export type ToolParamType = File | Value | Table | Graph;

export function isValue(param: ToolParamType): param is Value {
  return param.type === 'Value';
}

export function isFile(param: ToolParamType): param is File {
  return param.type === 'File';
}

export function isImageFile(parameter: ToolParam): boolean {
  return (
    isFile(parameter.param) &&
    parameter.param.supportedMediaTypes.some(mediaType => {
      const type = mediaType.trim().toLowerCase().replace(/^\./, '');
      return type.startsWith('image/') || IMAGE_FILE_EXTENSIONS.has(type);
    })
  );
}

const IMAGE_FILE_EXTENSIONS = new Set([
  'avif',
  'bmp',
  'gif',
  'heic',
  'heif',
  'ico',
  'jpeg',
  'jpg',
  'png',
  'svg',
  'tif',
  'tiff',
  'webp',
]);

export function isTable(param: ToolParamType): param is Table {
  return param.type === 'Table';
}

export function isGraph(param: ToolParamType): param is Graph {
  return param.type === 'Graph';
}

export type File = {
  type: 'File';
  supportedMediaTypes: string[];
  value: null | number | number[]; // File ID or List of File IDs when output
};

export type DataTypeName =
  'string' | 'textArea' | 'bool' | 'number' | 'date' | 'date_span' | 'money' | 'custom_value';
export type DataType = string | boolean | number | DateSpan | Money | CustomValue | EChartsOption;
export type DateSpan = { startDate: string; endDate: string };
export type Currency = 'EUR' | 'USD';
export type Money = { value: number; currency: Currency };

export type CustomValue = {
  name: string;
  value: CustomObject[];
};

export type CustomObject = {
  name: string;
  value: DataType;
  type: DataTypeName;
  isModifiable: boolean;
};

export type Value = {
  type: 'Value';
  dataType: DataTypeName;
  value: DataType | DataType[];
  placeholder: string;
  options?: DataType[];
};

export type TableEditMode = 'popup' | 'table';

export type OutputType = DataType | DataType[] | Record<string, unknown>;

export type Table = {
  type: 'Table';
  dataType: DataTypeName;
  value: DataType | DataType[];
  editType?: TableEditMode;
  columnDef: Array<TableColumn>;
};

export type Graph = {
  type: 'Graph';
  dataType: DataTypeName;
  value: EChartsOption;
};

export type TableJob = Job & { routerLink: string; stepCounter: string };
export type Job = ToolJob | ToolchainJob;

export type ToolJob = {
  toolJobId: number;
  userId: number;
  tool: Pick<Tool, 'toolId'>;
  status: ToolJobStatus;

  created: string;
  started?: string;
  modified: string;

  funcInput: Record<string, unknown>[];
  results: string | Record<string, unknown>[];
  outPath: string;
};

export type ToolJobLogs = {
  toolJobId: number;
  logs: string[];
};

export type ToolJobStatus = 'Done' | 'Started' | 'Error' | 'Pending';

export type UserInfo = {
  userId: number;
  userName: string;
  role: Role;
  canAccessChat: boolean;
};

export enum Role {
  Admin = 1,
  Developer = 2,
  User = 3,
}

export const RoleToStringMapping: Record<Role, string> = {
  [Role.Admin]: 'Admin',
  [Role.Developer]: 'Developer',
  [Role.User]: 'User',
};

export const StringToRoleMapping: Record<string, Role> = {
  ['Admin']: Role.Admin,
  ['Developer']: Role.Developer,
  ['User']: Role.User,
};

export type UserToolsRegistration = {
  user: UserInfo;
  tools: Tool[];
};

export type ToolUsersRegistration = {
  tool: Tool;
  users: UserInfo[];
};

export type ToolRegistration = {
  created: string;
  isActive: boolean;
  modified: string;
  registrationId: number;
  tool: Tool;
  user: UserInfo;
};

export type Message = {
  timestamp: string;
  message: string;
};

export type Log = {
  log: Message[];
};
